import { useQuery, UseQueryOptions } from 'react-query';
import { AxiosPrivate } from '../utils/axios';
import { isEmpty } from 'lodash';

export interface DownloadFile {
  name: string;
  blobUrl: string;
}

const pullingInterval = parseInt(
  process.env.GATSBY_COMMENTS_DOWNLOAD_PULLING_INTERVAL,
  10,
);

const maxRetries = parseInt(process.env.GATSBY_COMMENTS_DOWNLOAD_MAX_RETRIES);

/**
 * Hook for polling download status using React Query
 *
 * Polls the server for download completion. Returns the file when ready,
 * or false if still processing.
 *
 */
export function useDownloadProgress(
  referenceId: string | null,
  clearReferenceId: () => void,
) {
  const { data, isLoading, error, status } = useQuery<DownloadFile | false>(
    ['downloadProgress', referenceId],
    async () => {
      const { data } = await AxiosPrivate.get<DownloadFile | null>(
        `/comments/download/${referenceId}`,
      );
      if (isEmpty(data)) {
        return null;
      }
      return data;
    },
    {
      enabled: !!referenceId,
      refetchInterval: pullingInterval,
      refetchIntervalInBackground: false,
      retry: maxRetries,
      retryDelay: attemptIndex => pullingInterval * (attemptIndex + 1),
      refetchOnWindowFocus: false,
      staleTime: 0,
      onSuccess: file => {
        // If a file object is returned (not null), download it
        if (file && typeof file !== 'boolean') {
          const downloadLink = document.createElement('a');
          downloadLink.href = file.blobUrl;
          downloadLink.download = file.name;
          downloadLink.click();
          clearReferenceId();
        }
      },
    },
  );

  return {
    data,
    status,
    isLoading,
    error,
  };
}
